import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  Observable,
  Subject,
  throwError,
  timeout,
} from 'rxjs';
import { IonicService } from './ionic.service';
import { UserService } from './user.service';
import { environment } from 'src/environments/environment';
import { UserStore } from '../store/user.store';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public STORAGE_NAME = 'user';
  public user: any;

  //User
  public loginSubject: Subject<any> = new Subject<any>();
  public updateUserSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    this.getLoggedUserFromLocalStorage(),
  );

  //UserLevel
  public userLevelSubject: BehaviorSubject<number> =
    new BehaviorSubject<number>(this.getUserLevelFromLocalStorage());
  public userLevel$ = this.userLevelSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  apiUrl = environment.apiUrl;
  userStore = inject(UserStore);

  constructor(
    public http: HttpClient,
    public ionicService: IonicService,
    public userService: UserService,
    public router: Router,
  ) { }

  getUser() {
    return this.userStore.user();
    // const storedValue = localStorage.getItem(this.STORAGE_NAME);
    // return storedValue ? JSON.parse(storedValue) : null;
  }

  login(params: any) {
    const aux = {
      email: params.email,
      password: params.password,
    };
    return this.http
      .post<any>(`${this.apiUrl}/auth/login`, aux, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(
            () => new Error(error.error.message || 'Error during login'),
          );
        }),
      );
  }

  ensureWalletReadiness(userId: string) {
    return this.http.post<{
      ready: boolean;
      walletCreated: boolean;
      erc20ApprovalRepaired: boolean;
      rwaApprovalRepaired: boolean;
      walletAddress: string;
    }>(`${this.apiUrl}/users/ensure-wallet-readiness/${userId}`, {});
  }

  async logout(message?: string) {
    try {
      // Clear user data
      localStorage.removeItem(this.STORAGE_NAME);
      localStorage.removeItem('sessionStarted');
      this.user = null;
      this.userStore.logOut();
      this.updateUserSubject.next(null);
      this.userLevelSubject.next(0);

      // Navigate to home first
      await this.router.navigate(['/home']);

      // Show alert if message provided
      if (message) {
        setTimeout(() => {
          this.ionicService.showAlert('Sesión cerrada', message, [{ text: 'Aceptar' }]);
        }, 100);
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  signUp(params: any) {
    return this.http.post<any>(`${this.apiUrl}/auth/register`, params, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  signUpAdmin(params: any) {
    return this.http.post<any>(`${this.apiUrl}/auth/register-admin`, params, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  loginSave(user: any, tokens: any, refreshToken: any, tokenAuth: any) {
    this.userStore.updateUser({
      ...user,
      tokens,
      refreshToken,
      tokenAuth,
    });
    localStorage.setItem('sessionStarted', 'true');

    this.updateUserSubject.next(user);
    this.userLevelSubject.next(user.userLevel?.userLevel);

    this.saveUserLevelToLocalStorage(user.userLevel?.userLevel);
  }

  private getUserLevelFromLocalStorage(): number {
    const storedData = localStorage.getItem(this.STORAGE_NAME);
    if (storedData) {
      const aux = JSON.parse(storedData);
      return aux?.userLevel?.userLevel ?? 0;
    }
    return 0;
  }

  public saveUserLevelToLocalStorage(userLevel: any): void {
    const currentUser = this.getUser();
    const normalizedLevel =
      typeof userLevel === 'number' ? userLevel : userLevel?.userLevel ?? 0;

    if (currentUser && currentUser.userLevel) {
      const updatedUser = {
        ...currentUser,
        userLevel: userLevel?.uuid ? { ...userLevel } : {
          ...currentUser.userLevel,
          userLevel: normalizedLevel,
        },
      };

      this.userStore.updateUser(updatedUser);
    }

    this.userLevelSubject.next(normalizedLevel);
  }
  checkIfLogged() {
    return this.userStore.user();
  }

  getLoggedUser() {
    return this.user;
  }

  public getLoggedUserFromLocalStorage(): any {
    // First try UserStore (most up-to-date)
    const userStoreData = this.userStore?.user();
    if (userStoreData) {
      return userStoreData;
    }
    
    // Fallback to localStorage
    const storedData = localStorage.getItem(this.STORAGE_NAME);
    if (storedData) {
      try {
        return JSON.parse(storedData);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        localStorage.removeItem(this.STORAGE_NAME);
      }
    }
    
    return null;
  }

  async getUserLevel(uuid: string, timeoutMs = 30000) {
    const userLevel = await firstValueFrom(this.userService.getUserLevel(uuid).pipe(timeout(timeoutMs)));
    this.saveUserLevelToLocalStorage(userLevel);
    return userLevel;
  }

  public updateUserPhone(phone: any): Observable<any> {
    const params = {
      phone,
      token: JSON.parse(localStorage.getItem('user')!).tokenAuth,
    };
    return this.http
      .post<any>(`${this.apiUrl}/auth/update-phone`, params, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(
            () =>
              new Error(error.error?.message || 'An unexpected error occurred'),
          );
        }),
      );
  }

  verifyUserPhone(code: string) {
    const params = {
      code,
      token: JSON.parse(localStorage.getItem('user')!).tokenAuth,
    };
    return this.http.post<any>(`${this.apiUrl}/auth/verify-phone`, params).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(
          () =>
            new Error(error.error.message || 'An unexpected error occurred'),
        );
      }),
    );
  }

  forgotPass(param: any) {
    return this.http.post<any>(`${this.apiUrl}/auth/forgot-password`, param);
  }

  registerConfirm(params: { username: string; confirmationCode: string }) {
    return this.http.post(`${this.apiUrl}/auth/register-confirm`, params);
  }

  forgotPasswordConfirm(params: {
    email: string;
    code: string;
    newPassword: string;
  }) {
    return this.http.post(
      `${this.apiUrl}/auth/confirm-forgot-password`,
      params,
    );
  }

  createSMSDocumentation(userId: string, canUpgradeToLevelOne: boolean) {
    let user = JSON.parse(localStorage.getItem('user')!);
    const params = {
      userId,
      canUpgradeToLevelOne: canUpgradeToLevelOne,
    };
    return this.http.post<any>(
      `${this.apiUrl}/user-level/create-sms-documentation`,
      params,
    );
  }

  refreshToken(refreshToken: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/refresh-token`, {
      refreshToken: refreshToken
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(
          () => new Error(error.error?.message || 'Error refreshing token')
        );
      })
    );
  }

  updateUserCoins(coins: number, onlyUpdate: boolean) {
    const currentUser = this.getLoggedUserFromLocalStorage();
    if (onlyUpdate && currentUser) {
      currentUser.coins = coins;
      localStorage.setItem(this.STORAGE_NAME, JSON.stringify(currentUser));
      this.userStore.updateUser(currentUser);
    }
    if (!onlyUpdate && currentUser) {
      currentUser.coins = (currentUser.coins || 0) - coins;
      localStorage.setItem(this.STORAGE_NAME, JSON.stringify(currentUser));
      this.userStore.updateUser(currentUser);
    }
  }
}
